import { useEffect, useState } from 'react'
import { Box, Heading, Spinner, Button } from 'theme-ui'
import { useWorker } from '@koale/useworker'
import useFetch from 'use-http'
import { ScrollSync, ScrollSyncPane } from 'react-scroll-sync'

import { useUserContext } from '../context/user'
import { apiUrl } from '../environment'

import calculateRankings from '../components/pages/leaderboard/calculateRankings'
import Row from '../components/pages/leaderboard/Row'
import GridHeader from '../components/pages/leaderboard/GridHeader'

import Header from '../components/Header'
import Meta from '../components/Meta'

export default function Leaderboard() {
    const { loading, error, data } = useFetch(
        `${apiUrl}/leaderboard/holders`,
        []
    )
    const [rankings, setRankings] = useState([])
    const [totalVisible, setTotalVisible] = useState(50)
    const [order, setOrder] = useState('ranking')
    const [ascending, setAscending] = useState(true)

    const { address } = useUserContext()

    const [calculateWorker, { status }] = useWorker(
        calculateRankings,
        {
            remoteDependencies: [
                'https://cdnjs.cloudflare.com/ajax/libs/ethers/5.6.9/ethers.umd.min.js',
            ],
            autoTerminate: true,
        },
        []
    )

    const loadMore = () => {
        setTotalVisible(totalVisible + 50)
    }

    useEffect(() => {
        if (data) {
            calculateWorker(data)
                .then((result) => {
                    setRankings(result)
                })
                .catch(function (reason) {
                    console.log(reason)
                })
        }
    }, [data])

    const myData = address && rankings.find((r) => r.address === address)

    const onSort = (value) => {
        if (value === order) {
            setAscending(!ascending)
        }
        setOrder(value)
    }

    const width = 1250
    const isLoading = loading || status !== 'SUCCESS'
    const ordered = rankings.sort(function (a, b) {
        return ascending ? a[order] - b[order] : b[order] - a[order]
    })
    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Meta
                title="Leaderboard | TinyFaces NFT"
                description="Explore our curated selection of top TinyFaces NFT holders featured on our leaderboard. This exclusive list not only highlights their standout NFTs but may also provide contact information. If you're interested in acquiring a specific TinyFaces NFT, this feature could facilitate direct communication with the owner, opening the door to potential purchase opportunities."
                keywords="NFT, Digital Art, Blockchain Art, TinyFaces NFT, NFT leaderboard, NFT rankings, digital art NFT"
            />
            <Header />
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    alignItems: 'center',
                    py: [6, 6, 8],
                    flex: 1,
                }}
            >
                <Heading
                    as="h1"
                    sx={{
                        fontFamily: 'heading',
                        fontSize: [6, 6, 8],
                        lineHeight: 1.1,
                        pb: [7, 7, 8],
                        px: [3, 5],
                    }}
                >
                    Leaderboard
                </Heading>
                {isLoading ? (
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            width: '100%',
                            pb: '10%',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <Spinner strokeWidth={2} />
                    </Box>
                ) : (
                    <ScrollSync>
                        <Box
                            sx={{
                                width: '100%',
                                maxWidth: width,
                                position: 'relative',
                                overscrollBehavior: 'contain',
                                mx: 'auto',
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 1,
                                    borderBottomStyle: 'solid',
                                    borderBottomColor: 'border',
                                    borderBottomWidth: '1px',
                                }}
                            >
                                <ScrollSyncPane>
                                    <Box
                                        sx={{
                                            overflowX: 'hidden',
                                            overflowY: 'hidden',
                                            WebkitOverflowScrolling: 'touch',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                minWidth: width,
                                                gridTemplateColumns:
                                                    '40px auto 115px 115px 115px 115px 115px 115px',
                                                display: 'grid',
                                            }}
                                        >
                                            <GridHeader
                                                order={order}
                                                ascending={ascending}
                                                onSort={(key) => onSort(key)}
                                            />
                                        </Box>
                                    </Box>
                                </ScrollSyncPane>
                            </Box>

                            {myData && (
                                <ScrollSyncPane>
                                    <Box
                                        sx={{
                                            overflow: 'auto',
                                            scrollSnapType: 'x mandatory',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                gridRowGap: '1px',
                                                display: 'grid',
                                                minWidth: width,
                                                gridTemplateColumns:
                                                    '40px auto 115px 115px 115px 115px 115px 115px',
                                                bg: 'border',
                                            }}
                                        >
                                            <Row
                                                highlight
                                                result={myData}
                                                key={myData.address}
                                            />
                                        </Box>
                                    </Box>
                                </ScrollSyncPane>
                            )}

                            <ScrollSyncPane>
                                <Box
                                    sx={{
                                        overflow: 'auto',
                                        scrollSnapType: 'x mandatory',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            gridRowGap: '1px',
                                            display: 'grid',
                                            minWidth: width,
                                            gridTemplateColumns:
                                                '40px auto 115px 115px 115px 115px 115px 115px',
                                            bg: 'border',
                                        }}
                                    >
                                        {ordered
                                            .slice(0, totalVisible)
                                            .map((result) => (
                                                <Row
                                                    result={result}
                                                    key={result.address}
                                                />
                                            ))}
                                    </Box>
                                </Box>
                            </ScrollSyncPane>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                }}
                            >
                                <Button
                                    sx={{ cursor: 'pointer' }}
                                    onClick={loadMore}
                                >
                                    Load more
                                </Button>
                            </Box>
                        </Box>
                    </ScrollSync>
                )}
            </Box>
        </Box>
    )
}
